import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Filter } from 'lucide-react';
import { AlertCard } from '../../components/alerts/AlertCard';
import { AlertClosureModal } from '../../components/alerts/AlertClosureModal';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/Spinner';
import { useAlerts } from '../../hooks/useAlerts';
import { LoadError } from '../../components/common/LoadError';
import type { AlertSeverity } from '../../types/alert';
import clsx from 'clsx';

type StatusFilter = 'all' | 'active' | 'closed';
type SeverityFilter = 'all' | AlertSeverity;

export default function AlertsPage() {
  const { alerts, criticalCount, isLoading, error, closeAlert } = useAlerts();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [closingAlertId, setClosingAlertId] = useState<string | null>(null);
  const [closeError, setCloseError] = useState<string | null>(null);

  useEffect(() => { document.title = 'Alertas | Paitron Radar'; }, []);

  const filtered = alerts.filter(a => {
    const matchStatus = statusFilter === 'all' || a.status === statusFilter;
    const matchSeverity = severityFilter === 'all' || a.severity === severityFilter;
    return matchStatus && matchSeverity;
  });

  const openClosureModal = (id: string) => setClosingAlertId(id);
  const handleConfirmClose = (id: string, reason: string) => {
    setCloseError(null);
    setClosingAlertId(null);
    // El cierre es optimista; si el backend lo rechaza la alerta vuelve sola
    // a su estado y aquí se dice por qué, en vez de reaparecer sin explicación.
    void closeAlert(id, reason).then(problem => setCloseError(problem));
  };

  return (
    <div className="alerts-page">
      {/* Header stats */}
      <div className="alerts-page__stats">
        <div className="alerts-page__stat alerts-page__stat--critical">
          <AlertTriangle size={20} />
          <div>
            <span className="alerts-page__stat-value">{criticalCount}</span>
            <span className="alerts-page__stat-label">Críticas activas</span>
          </div>
        </div>
        <div className="alerts-page__stat alerts-page__stat--medium">
          <AlertTriangle size={20} />
          <div>
            <span className="alerts-page__stat-value">
              {alerts.filter(a => a.status === 'active' && a.severity === 'medium').length}
            </span>
            <span className="alerts-page__stat-label">Medias activas</span>
          </div>
        </div>
        <div className="alerts-page__stat alerts-page__stat--closed">
          <CheckCircle2 size={20} />
          <div>
            <span className="alerts-page__stat-value">
              {alerts.filter(a => a.status === 'closed').length}
            </span>
            <span className="alerts-page__stat-label">Cerradas hoy</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="alerts-page__filters">
        <div className="alerts-page__filter-group">
          <Filter size={14} />
          {(['all', 'active', 'closed'] as StatusFilter[]).map(s => (
            <button
              key={s}
              id={`alerts-status-${s}`}
              className={clsx('filter-chip', statusFilter === s && 'filter-chip--active')}
              onClick={() => setStatusFilter(s)}
            >
              {{ all: 'Todas', active: 'Activas', closed: 'Cerradas' }[s]}
            </button>
          ))}
        </div>
        <div className="alerts-page__filter-group">
          {(['all', 'critical', 'medium', 'low'] as SeverityFilter[]).map(s => (
            <button
              key={s}
              id={`alerts-severity-${s}`}
              className={clsx('filter-chip', `filter-chip--${s}`, severityFilter === s && 'filter-chip--active')}
              onClick={() => setSeverityFilter(s)}
            >
              {{ all: 'Todas', critical: 'Crítica', medium: 'Media', low: 'Baja' }[s]}
            </button>
          ))}
        </div>
      </div>

      {closeError && (
        <p
          role="alert"
          style={{ margin: '0 0 0.75rem', padding: '0.625rem 0.875rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-md)', color: '#EF4444', fontSize: '12.5px', fontWeight: 600, display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}
        >
          <span>{closeError}</span>
          <button type="button" onClick={() => setCloseError(null)} aria-label="Descartar aviso" style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 700 }}>✕</button>
        </p>
      )}

      {/* Feed */}
      <div className="alerts-page__feed">
        {isLoading ? (
          <div className="alerts-page__loading">
            <Spinner size="lg" />
          </div>
        ) : error && alerts.length === 0 ? (
          <LoadError what="las alertas" error={error} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<CheckCircle2 size={48} />}
            title="Sin alertas"
            description="No hay alertas que coincidan con los filtros seleccionados."
          />
        ) : (
          filtered.map(alert => (
            <AlertCard
              key={alert.id}
              alert={alert}
              onClose={alert.status === 'active' ? openClosureModal : undefined}
            />
          ))
        )}
      </div>

      {/* Closure modal */}
      <AlertClosureModal
        alertId={closingAlertId ?? ''}
        isOpen={!!closingAlertId}
        onClose={() => setClosingAlertId(null)}
        onConfirm={handleConfirmClose}
      />
    </div>
  );
}
